import { useBrandLocale } from '../../../hooks/useBrandLocale';
import { useCarDistanceUnit } from '../../../hooks/useCarDistanceUnit';
import { formatFuelEconomy } from '../../../utils/units';
import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import ImageGallery from 'react-image-gallery';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faEnvelope,
  faCamera,
  faCar,
  faGasPump,
  faCog,
  faTachometerAlt,
  faPerson,
  faStar,
  faCheck,
  faEdit,
  faShareNodes,
  faArrowLeft,
  faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import { URLS } from '../../../../constants/urls';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useParams } from 'react-router-dom';
import { useGetCarQuery } from '../../../services/carService/__generated__/queries.generated';
import ViewDealerCarSkeleton from '../../../components/skeletons/ViewDealerCarSkeleton';

import 'react-image-gallery/styles/css/image-gallery.css';

const Container = styled.div`
  ${tw`flex flex-col bg-black text-white min-h-screen py-8 px-4 w-full items-center box-border`}
`;

const PageContainer = styled.div`
  ${tw`text-sm text-text-tertiary w-full max-w-full min-w-0 md:w-3/4 box-border pb-8 md:pb-16`}
`;

const BackButton = styled.button`
  ${tw`flex items-center text-text-tertiary mb-4`}
  padding: 0.5rem 1rem;
  border: 1px solid #4b5563;
  border-radius: 0.25rem;

  &:hover {
    ${tw`text-white`}
    border-color: #6b7280;
  }
`;

const VehicleHeader = styled.div`
  ${tw`flex flex-col lg:flex-row justify-between items-start lg:items-center mb-2`}
`;

const VehicleTitle = styled.div`
  ${tw`flex flex-col`}
`;

const Title = styled.h1`
  ${tw`text-3xl font-bold text-white`}
`;

const Subtitle = styled.h2`
  ${tw`text-lg text-text-tertiary`}
`;

const PriceInfo = styled.div`
  ${tw`flex flex-col items-end mt-4 lg:mt-0`}
`;

const Price = styled.div`
  ${tw`text-3xl font-bold text-white`}
`;

const Mileage = styled.div`
  ${tw`text-xl font-semibold text-white`}
`;

const ActionButtons = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-3 gap-4 mb-8`}
`;

const ActionButton = styled.button`
  ${tw`flex items-center justify-center py-3 px-4 bg-black text-white rounded-md transition-colors`}
  border: 1px solid #4B5563;

  &:hover {
    ${tw`bg-gray-700`}
    border-color: #6b7280;
  }
`;

const ContentContainer = styled.div`
  ${tw`grid grid-cols-1 lg:grid-cols-3 gap-6 box-border`}
  position: relative;
  z-index: 2;
`;

const PhotosSection = styled.div`
  ${tw`col-span-1 lg:col-span-2 bg-black rounded-lg overflow-hidden box-border w-full max-w-full min-w-0`}
  border: 1px solid #374151;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const MainPhoto = styled.div`
  ${tw`relative w-full h-auto bg-gray-700`}

  img {
    ${tw`w-full h-full object-cover`}
  }
`;

const PhotoNavigation = styled.div`
  ${tw`absolute inset-0 flex items-center justify-between`}
`;

const NavButton = styled.button`
  ${tw`bg-black bg-opacity-50 text-white p-3 rounded-full mx-4`}
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const PhotosButton = styled.button`
  ${tw`flex items-center gap-2 bg-black text-white py-2 px-4 rounded-md m-4 absolute top-0 left-0`}
  border: 1px solid #4B5563;
`;

const ShareSection = styled.div`
  ${tw`flex justify-between items-center p-4 bg-black text-white rounded-lg mt-4`}
  border: 1px solid #374151;
`;

const VirtualButtons = styled.div`
  ${tw`flex flex-wrap gap-2 mt-4`}
`;

const VirtualButton = styled.button`
  ${tw`bg-black text-white py-2 px-4 rounded-md border border-gray-700`}

  &:hover {
    ${tw`bg-gray-700`}
  }
`;

const InfoSection = styled.div`
  ${tw`col-span-1 bg-black text-white rounded-lg overflow-hidden box-border`}
  border: 1px solid #374151;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const InfoHeader = styled.div`
  ${tw`p-4 text-xl font-bold border-b border-gray-700`}
`;

const InfoList = styled.div`
  ${tw`p-4`}
`;

const InfoItem = styled.div`
  ${tw`flex items-center py-3 border-b border-gray-700 last:border-b-0`}
`;

const InfoIcon = styled.div`
  ${tw`mr-4 text-text-tertiary w-8`}
`;

const InfoLabel = styled.div`
  ${tw`text-text-tertiary text-sm`}
`;

const InfoValue = styled.div`
  ${tw`font-medium ml-auto text-white`}
`;

const ContactSection = styled.div`
  ${tw`col-span-1 lg:col-span-3 bg-black text-white rounded-lg box-border`}
  margin-bottom: 60px;
  border: 1px solid #374151;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ContactForm = styled.div`
  ${tw`p-4 bg-black text-text-tertiary m-6 rounded-lg h-auto box-border`}
  border: 1px solid #4B5563;
`;

const Badge = styled.div<{ status: string }>`
  ${tw`bg-black text-white py-2 px-4 rounded-md h-[40px] pl-4 pr-4 flex items-center justify-center`}
  ${props => props.status === 'SOLD' && tw`bg-gray-600`}
  ${props => props.status === 'PENDING' && tw`bg-red-600`}
`;

const ContactLabel = styled.span`
  ${tw`pl-3`}
`;

const Label = styled.span`
  ${tw`text-text-tertiary text-sm`}
`;

const Feature = styled.div`
  ${tw`flex items-center gap-1 text-sm capitalize mb-2 text-text-tertiary`}
`;

const FeaturesContainer = styled.div`
  ${tw`grid grid-cols-2 gap-2`}
`;

const StatusBadge = styled.div<{ status: string }>`
  ${tw`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-2`}
  ${({ status }) => {
    switch (status) {
      case 'SOLD':
        return tw`bg-gray-600 text-white`;
      case 'PENDING':
        return tw`bg-yellow-600 text-white`;
      default:
        return tw`bg-black text-white`;
    }
  }}
`;

const CarfaxButton = styled(Link)`
  ${tw`bg-black text-white py-2 px-4 rounded-md transition-colors flex items-center gap-2 absolute top-0 right-0 m-4`}

  &:hover {
    ${tw`bg-blue-700`}
  }
`;

const FullWidthCarfaxButton = styled(Link)`
  ${tw`bg-black text-white py-3 px-4 rounded-md transition-colors flex items-center justify-center gap-2 w-full mt-4`}

  &:hover {
    ${tw`bg-blue-700`}
  }
`;

export default function ViewDealerCarPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { carId } = useParams<{ carId: string }>();
  // Spec 028: the odometer in its own unit (D11); fuel economy in L/100 km on a metric brand (D7).
  const { odometerUnit } = useCarDistanceUnit(carId);
  const { distanceUnit: brandUnit } = useBrandLocale();

  const { data, loading, error } = useGetCarQuery({
    variables: {
      id: carId ?? '',
    },
    skip: !carId,
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleShareClick = () => {
    const currentUrl = window.location.origin + location.pathname;

    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        toast.success('Link copied to clipboard!', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      })
      .catch(err => {
        toast.error('Failed to copy link', {
          position: 'bottom-right',
          autoClose: 3000,
        });
      });
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    img.src = '/fallback-image.jpg'; // Add a fallback image to your public folder
  };

  const handleSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const handleScreenChange = (fullscreen: boolean) => {
    setIsFullscreen(fullscreen);
  };

  if (loading) return <ViewDealerCarSkeleton />;

  if (!data?.car) {
    return <div>Loading...</div>;
  }

  const images =
    data.car.images?.map(img => ({
      original: `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${img.imageUrl}`,
      thumbnail: `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${img.imageUrl}`,
      originalAlt: `${data.car?.make} ${data.car?.model}`,
      thumbnailAlt: `Thumbnail of ${data.car?.make} ${data.car?.model}`,
    })) || [];

  return (
    <Container>
      <PageContainer>
        <BackButton onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to listings
        </BackButton>

        <VehicleHeader>
          <VehicleTitle>
            <Title>{data?.car?.name}</Title>
            <Subtitle>{data?.car?.productTypeDetails?.type || 'Car'}</Subtitle>
          </VehicleTitle>
          <PriceInfo>
            <StatusBadge status={data?.car?.status || 'AVAILABLE'}>
              {data?.car?.status || 'AVAILABLE'}
            </StatusBadge>
            <Price>${data?.car?.listedSalePrice}</Price>
            <Label>+ DMV fees</Label>
            <Mileage>
              {data?.car?.mileage} {odometerUnit === 'km' ? 'km' : 'miles'}
            </Mileage>
            <Label>approx.</Label>
          </PriceInfo>
        </VehicleHeader>

        <ActionButtons>
          <ActionButton onClick={() => window.open(`tel:${data?.car?.dealer?.phone}`)}>
            <FontAwesomeIcon icon={faPhone} className="mr-2" />
            <ContactLabel>Call Us</ContactLabel>
          </ActionButton>
          <ActionButton onClick={() => window.open(`mailto:${data?.car?.dealer?.email}`)}>
            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
            <ContactLabel>Email Us</ContactLabel>
          </ActionButton>
          <ActionButton>
            <FontAwesomeIcon icon={faEdit} className="mr-2" />
            <ContactLabel>Description</ContactLabel>
          </ActionButton>
        </ActionButtons>

        <ContentContainer>
          <PhotosSection>
            <MainPhoto>
              <div className="relative w-full h-[400px] md:h-[500px]">
                {React.createElement(ImageGallery as any, {
                  items: images,
                  showPlayButton: false,
                  showFullscreenButton: true,
                  showNav: true,
                  showThumbnails: true,
                  useBrowserFullscreen: true,
                  lazyLoad: true,
                  thumbnailPosition: 'bottom',
                  slideInterval: 3000,
                  slideDuration: 450,
                  startIndex: currentIndex,
                  onSlide: handleSlide,
                  onScreenChange: handleScreenChange,
                  onErrorImageURL: '/fallback-image.jpg',
                  additionalClass: `car-gallery ${isFullscreen ? 'fullscreen' : ''}`,
                })}
              </div>
              <PhotoNavigation>
                <NavButton>←</NavButton>
                <NavButton>→</NavButton>
              </PhotoNavigation>
              <PhotosButton>
                <FontAwesomeIcon icon={faCamera} /> Photos ({data?.car?.images?.length})
              </PhotosButton>
              <CarfaxButton to={`/carfax/${data?.car?.id}`}>
                <FontAwesomeIcon icon={faFilePdf} />
                View CARFAX
              </CarfaxButton>
            </MainPhoto>

            <ShareSection>
              <div>Share Vehicle</div>
              <FontAwesomeIcon
                icon={faShareNodes}
                onClick={handleShareClick}
                className="cursor-pointer hover:text-gray-300"
              />
            </ShareSection>
          </PhotosSection>

          <InfoSection>
            <InfoHeader>Vehicle Info</InfoHeader>
            <InfoList>
              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faCheck} />
                </InfoIcon>
                <div>
                  <InfoLabel>Title Status</InfoLabel>
                  <div>CLEAN</div>
                </div>
              </InfoItem>
              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faCar} />
                </InfoIcon>
                <div>
                  <InfoLabel>Condition</InfoLabel>
                  <div>Used</div>
                </div>
              </InfoItem>
              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faTachometerAlt} />
                </InfoIcon>
                <div>
                  <InfoLabel>{brandUnit === 'km' ? 'Highway' : 'Hwy Mpg'}</InfoLabel>
                  <div>{formatFuelEconomy(data?.car?.hwyMpg, brandUnit)}</div>
                </div>
              </InfoItem>
              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faCog} />
                </InfoIcon>
                <div>
                  <InfoLabel>Transmission</InfoLabel>
                  <div>{data?.car?.gearType}</div>
                </div>
              </InfoItem>
              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faPerson} />
                </InfoIcon>
                <div>
                  <InfoLabel>Capacity</InfoLabel>
                  <div>{data?.car?.seats} seats</div>
                </div>
              </InfoItem>
              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faGasPump} />
                </InfoIcon>
                <div>
                  <InfoLabel>Fuel</InfoLabel>
                  <div>{data?.car?.gas}</div>
                </div>
              </InfoItem>
              <InfoItem>
                <InfoIcon>
                  <FontAwesomeIcon icon={faStar} />
                </InfoIcon>
                <div>
                  <InfoLabel>Features</InfoLabel>
                  <FeaturesContainer>
                    {data?.car?.features?.map(feature => (
                      <Feature key={feature}>
                        <FontAwesomeIcon icon={faCheck} className="text-green-500" />
                        <span>{feature.toLowerCase().replace(/_/g, ' ')}</span>
                      </Feature>
                    ))}
                  </FeaturesContainer>
                </div>
              </InfoItem>
              <InfoItem>
                <InfoIcon>#</InfoIcon>
                <div>
                  <InfoLabel>Stock #</InfoLabel>
                  <div>{data?.car?.id}</div>
                </div>
              </InfoItem>
              <InfoItem>
                <InfoIcon>VIN</InfoIcon>
                <div>
                  <InfoLabel>VIN</InfoLabel>
                  <div>{data?.car?.vin}</div>
                </div>
              </InfoItem>
            </InfoList>
          </InfoSection>

          <ContactSection>
            <InfoHeader>Description</InfoHeader>
            <ContactForm>
              <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                {data?.car?.description}
              </ReactMarkdown>
              <FullWidthCarfaxButton to={`/carfax/${data?.car?.id}`}>
                <FontAwesomeIcon icon={faFilePdf} />
                View CARFAX Report
              </FullWidthCarfaxButton>
            </ContactForm>
          </ContactSection>
        </ContentContainer>
      </PageContainer>
    </Container>
  );
}
